import { ColumnProps } from "antd/lib/table";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PaymentDeliveryWorkerContext from "../../../context/payment-delivery-worker/context";
import { IPaymentDeliveryWorker } from "../../../models/payment-delivery-worker/response";
import Space from "../../general/antd/space";
import ViewBtn from "../../general/table-components/actions/view-btn";
import EditBtn from "../../general/table-components/actions/edit-btn";
import DeleteBtn from "../../general/table-components/actions/delete-btn";
import eventManager, { EVENT_SUCCESS } from "../../../utils/events";
import { ISort, IStaticFilter } from "../../../models/base/base-query";
import { tableOnChange } from "../../../utils/helpers/table-sorts-filters";
import Table from "../../general/table-components/table";
import moment from "moment";
import Tag from "../../general/antd/tag";
import { Tag as AntdTag } from "antd";
import {
  getPaymentStatus,
  getPaymentStatusColor,
} from "../../../models/payment-delivery-worker/enum";
import  styles from './styles.module.scss';
interface IProps { }

const PaymentDeliveryWorkerTable: React.FC<IProps> = (props) => {
  const userPermissions = localStorage?.getItem("permisionStoroge")?.split(",");

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, actions, loading, query } = useContext(
    PaymentDeliveryWorkerContext
  );

  const columns: ColumnProps<IPaymentDeliveryWorker>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      sorter: {
        multiple: 1,
      },
      width: 100,
      align: "center",
    },
    {
      title: t("emplyee"),
      dataIndex: "user",
      align: "center",
      key: "user",
      render: (_, record) => {
        return (
          <>
            {record?.user?.user?.firstName +
              "  " +
              record?.user?.user?.lastName }
          </>
        );
      },
    },
    {
      title: t("delivery_worker"),
      dataIndex: "delivery_worker",
      align: "center",
      key: "delivery_worker",
      render: (_, record) => {
        return (
          <>
            {record?.driver?.firstName + " " + record?.driver?.lastName}
          </>
        );
      },
    },
    {
      title: t("value"),
      dataIndex: "value",
      key: "value",
      align: "center",
      sorter: {
        multiple: 2,
      },
      render: (val: number) => val != null ? Number(val).toFixed(2) : "-",
    },
    {
      title: t("payment_type"),
      dataIndex: "paymentType",
      key: "paymentType",
      align: "center",
      render: (paymentType: string | null) => {
        if (!paymentType) return "-";
        return (
          <AntdTag color={paymentType === "trip" ? "blue" : "green"}>
            {t(paymentType === "trip" ? "trip" : "order")}
          </AntdTag>
        );
      },
    },
    {
      title: t("reference_number"),
      key: "referenceNumber",
      align: "center",
      render: (_: any, record: IPaymentDeliveryWorker) => {
        if (record.tripNumber) return record.tripNumber;
        if (record.orderNumber) return record.orderNumber;
        return "-";
      },
    },
    {
      title: t("expected_value"),
      dataIndex: "expectedValue",
      key: "expectedValue",
      align: "center",
      render: (val: number | null) => val != null ? Number(val).toFixed(2) : "-",
    },
    {
      title: t("driver_fee_difference"),
      dataIndex: "driverFeeDifference",
      key: "driverFeeDifference",
      align: "center",
      render: (diff: number | null) => {
        if (diff == null) return "-";
        const numDiff = Number(diff);
        const color = numDiff > 0 ? "red" : numDiff < 0 ? "green" : "default";
        return <AntdTag color={color}>{numDiff > 0 ? "+" : ""}{numDiff.toFixed(2)}</AntdTag>;
      },
    },
    {
      title: t("receipt_number"),
      dataIndex: "receiptNumber",
      key: "receipt_number",
      align: "center",
    },
    {
      title: t("payment_status"),
      dataIndex: "isConfirmed",
      width: 200,
      align: "center",
      key: "isConfirmed",
      render: (isConfirmed) => {
        return (
          <Tag
            title={getPaymentStatus(isConfirmed)}
            color={getPaymentStatusColor(isConfirmed)}
          />
        );
      },
    },
    {
      title: t("note"),
      dataIndex: "note",
      key: "note",
      align: "center",

    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      sorter: {
        multiple: 3,
      },
      render: (createdAt) => {
        return moment(createdAt).format("YYYY-MM-DD");
      },
    },
  ];

  const columnNoAction: ColumnProps<IPaymentDeliveryWorker>[] = [
    ...columns,
    {
      title: t("point"),
      dataIndex: "collectionPoint",
      key: "collectionPoint",
      align: "center",
      render: (_, record) => {
        return <>{record?.collectionPoint?.name}</>;
      },
    },
  ];

  const columnAction: ColumnProps<IPaymentDeliveryWorker>[] = [
    ...columns,
    {
      title: t("actions"),
      width: 220,
      dataIndex: "actions",
      fixed: "right",
      align: "center",
      key: "x",
      render: (_: any, record) => (
        <Space>
          <ViewBtn
              onClick={(e) => {
                const url = `/payment-delivery-worker/details/${record?.id}`;
                if (e.ctrlKey || e.metaKey) {
                  window.open(url, "_blank");
                } else {
                  navigate(url);
                }
              }}
            />
            <EditBtn
              onClick={(e) => {
                const url = `/payment-delivery-worker/update/${record?.id}`; 
                if (e.ctrlKey || e.metaKey) {
                  window.open(url, "_blank");
                } else {
                  navigate(url);
                }
              }}
            />
          <DeleteBtn
            disabled={record?.isConfirmed == 1 ? true : false}
            onConfirm={async () => {
              await actions.deletePaymentDeliveryWorker(record?.id);
              eventManager.emit(EVENT_SUCCESS);
            }}
            loading={loading.includes("delete")}
          />
        </Space>
      ),
    },
  ];
  // Filters && Sorts
  let tableFiltersProps = {};
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [, setStaticFilters] = useState<IStaticFilter[]>(query?.filters ?? []);

  useEffect(() => {
    sorts.length > 0 &&
      actions.setQuery({
        ...query,
        orders: sorts,
      });
  }, [sorts]);

  return (
    <>
      <Table<any>
        rowKey="id"
        showPagination={true}
        columns={
          userPermissions?.includes("can_view_pads")
            ? columnAction
            : columnNoAction
        }
        className={styles.table}
        dataSource={list?.data ?? []}
        loading={loading.includes("list") || list == undefined}
        size="small"
        total={list?.total}
        pageSize={query.perPage}
        page={query.page}
        onPaginationChange={(page, pageSize) => {
          actions.setQuery({
            ...query,
            page,
            perPage: pageSize,
          });
        }}
        scroll={{
          x: 2000,
        }}
        onChange={(_, tableFilters, tabelSorters) => {
          tableOnChange(
            tableFilters,
            tabelSorters,
            tableFiltersProps,
            setSorts,
            setStaticFilters
          );
        }}
      />
    </>
  );
};

export default PaymentDeliveryWorkerTable;
